import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FirestoreDataService } from '../core/firestore-data.service';
import { WorkshopWork, VehicleInspection, DamageMarker } from '../core/models';

@Component({
  selector: 'app-inspection',
  templateUrl: './inspection.component.html',
  styleUrls: ['./inspection.component.scss'],
  standalone: false
})
export class InspectionComponent implements OnInit {
  @ViewChild('bikeModel') modelViewer!: ElementRef;
  
  workId: string = '';
  caseNumber: string = '';
  workOrder: WorkshopWork | null = null;
  inspectionForm!: FormGroup;
  
  damageMarkers: DamageMarker[] = [];
  isSaving = false;
  isLoading = true;

  // Categorías estructuradas extraídas para MTB High-End / E-Bikes
  checkCategories = [
    {
      title: 'Transmisión y Sistema E-Bike',
      items: [
        { id: 'dt_chain', label: 'Cadena (Estiramiento/Desgaste)' },
        { id: 'dt_cassette', label: 'Cassette y Plato (Desgaste de dientes)' },
        { id: 'dt_derailleur', label: 'Desviador trasero (Tensión, Roldanas y Alineación)' },
        { id: 'dt_motor', label: 'Motor E-Bike (Ruidos extraños, asistencia)' },
        { id: 'dt_display', label: 'Display y Controles Remotos' }
      ]
    },
    {
      title: 'Suspensión y Dirección',
      items: [
        { id: 'susp_fork', label: 'Horquilla (Retenes, Presión de Aire, Rebote)' },
        { id: 'susp_shock', label: 'Shock trasero (Retenes, Aire, Comportamiento)' },
        { id: 'susp_linkage', label: 'Rodamientos del basculante (Linkage/Juego)' },
        { id: 'susp_headset', label: 'Juego de dirección (Holgura y suavidad)' }
      ]
    },
    {
      title: 'Frenos y Ruedas',
      items: [
        { id: 'bw_rotors', label: 'Rotores (Grosor, Alineación y Roces)' },
        { id: 'bw_levers', label: 'Manetas de freno y líneas (Tacto/Sangrado)' },
        { id: 'bw_spokes', label: 'Rayos/Radios (Tensión y Centrado)' },
        { id: 'bw_hubs', label: 'Mazas/Bujes (Rodamientos y Núcleo)' },
        { id: 'bw_tubeless', label: 'Líquido Tubeless (Estado y Válvulas)' }
      ]
    },
    {
      title: 'Cuadro y Contacto (Cockpit)',
      items: [
        { id: 'frame_dropper', label: 'Tija telescópica / Dropper (Retorno y Juego)' },
        { id: 'frame_grips', label: 'Puños / Grips (Desgaste)' },
        { id: 'frame_pedals', label: 'Pedales (Rodamientos y Pines)' },
        { id: 'frame_structure', label: 'Cuadro general (Fisuras, Pintura, Ridewrap)' }
      ]
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private firestoreService: FirestoreDataService
  ) {
    this.inspectionForm = this.fb.group({
      technician: [''],
      batteryHealthPct: [100], // Salud de batería en % para E-Bikes
      batteryChargeCycles: [0], // Ciclos de carga
      batteryStatus: [''], // Óptimo, Regular, Reemplazar
      tireFront: [null],
      tireRear: [null],
      brakePadFront: [null],
      brakePadRear: [null],
      checks: this.fb.group({}),
      generalNotes: ['']
    });

    // Construcción dinámica de los controles de revisión
    const checksGroup = this.inspectionForm.get('checks') as FormGroup;
    this.checkCategories.forEach(cat => {
      cat.items.forEach(item => {
        checksGroup.addControl(item.id, this.fb.group({
          status: [''],
          repaired: [false]
        }));
      });
    });
  }

  ngOnInit(): void {
    this.caseNumber = this.route.snapshot.paramMap.get('caseNumber') || this.route.snapshot.paramMap.get('workId') || '';
    if (this.caseNumber) {
      this.loadData();
    } else {
      this.isLoading = false;
    }
  }

 async loadData() {
    // 1. Buscamos el trabajo usando el N° de Caso (Ej: TRB-15)
    this.firestoreService.getWorkByCaseNumber(this.caseNumber).subscribe(work => {
      if (work && work.id) {
        this.workOrder = work;
        this.workId = work.id; // ¡RECUPERAMOS EL ID REAL DE FIRESTORE!

        // 2. Ahora sí, con el ID real, buscamos la inspección asociada
        this.firestoreService.getInspection(this.workId).subscribe(inspection => {
          this.isLoading = false;
          if (inspection) {
            this.inspectionForm.patchValue(inspection);
            this.damageMarkers = inspection.damages || [];
          }
        }, error => {
          console.error("Error cargando inspección", error);
          this.isLoading = false;
        });

      } else {
        alert(`No se encontró ninguna orden con el número ${this.caseNumber}`);
        this.isLoading = false;
      }
    }, error => {
      console.error("Error buscando la orden", error);
      this.isLoading = false;
    });
  }

  onModelClick(event: MouseEvent) {
    const viewer = this.modelViewer.nativeElement;
    const hit = viewer.positionAndNormalFromPoint(event.clientX, event.clientY);
    
    if (hit) {
      const partName = prompt('¿Qué parte del cuadro/bicicleta presenta daño?');
      if (!partName) return;
      
      const damageDesc = prompt('Describa el daño físico');
      if (!damageDesc) return;

      const newMarker: DamageMarker = {
        id: 'mark_' + new Date().getTime(),
        part: partName,
        damage: damageDesc,
        position: hit.position.toString(),
        normal: hit.normal.toString()
      };

      this.damageMarkers.push(newMarker);
    }
  }

  removeMarker(id: string) {
    this.damageMarkers = this.damageMarkers.filter(m => m.id !== id);
  }

  async saveInspection() {
    this.isSaving = true;
    const data: VehicleInspection = {
      ...this.inspectionForm.value,
      workId: this.workId,
      damages: this.damageMarkers
    };

    try {
      await this.firestoreService.saveInspection(this.workId, data);
      alert('Inspección técnica registrada exitosamente.');
    } catch (e) {
      alert('Error al guardar la inspección.');
    } finally {
      this.isSaving = false;
    }
  }

  printInspection() {
    window.print();
  }

  goBack() {
    this.router.navigate(['/dashboard']);
  }
}